"use client";

import { useEffect, useMemo, useState } from "react";
import { API_ENDPOINTS } from "@/constants/config";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const statusBadge = {
  draft: "bg-gray-100 text-gray-700",
  submitted: "bg-green-100 text-green-700",
  callback_requested: "bg-blue-100 text-blue-700",
  demo_requested: "bg-purple-100 text-purple-700",
};

export default function PublicLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [deletingId, setDeletingId] = useState("");

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const url = `${API_ENDPOINTS.superadminPublicLeads}?${params.toString()}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load leads");
      const json = await res.json();
      const leads = json?.data?.leads || [];
      setItems(Array.isArray(leads) ? leads : []);
      setError("");
    } catch (e) {
      setItems([]);
      setError(e?.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [status]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return items;
    return items.filter((x) => {
      const hay = [
        x.city,
        x.schoolName,
        x.phoneNumber,
        x.contactName,
        x.designation,
        x.leadSessionId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(s);
    });
  }, [items, q]);

  const handleDelete = async (id) => {
    if (!id) return;
    const confirmed = window.confirm("Delete this lead?");
    if (!confirmed) return;

    try {
      setDeletingId(id);
      const res = await fetch(API_ENDPOINTS.superadminPublicLead(id), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete lead");
      setItems((prev) => prev.filter((item) => item._id !== id));
      setError("");
    } catch (e) {
      setError(e?.message || "Failed to delete lead");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Public Leads</h1>
          <p className="text-sm text-gray-500">Landing bot + public chat leads</p>
        </div>
        <div className="flex gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm"
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="callback_requested">Callback requested</option>
            <option value="demo_requested">Demo requested</option>
          </select>
          <Button variant="outline" className="rounded-xl" onClick={fetchLeads}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search phone, school, city…"
          className="rounded-xl"
        />
        <Button className="rounded-xl bg-blue-600 hover:bg-blue-700" onClick={fetchLeads}>
          Search
        </Button>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-left font-semibold px-4 py-3">City</th>
                <th className="text-left font-semibold px-4 py-3">School</th>
                <th className="text-left font-semibold px-4 py-3">Phone</th>
                <th className="text-left font-semibold px-4 py-3">Name</th>
                <th className="text-left font-semibold px-4 py-3">Designation</th>
                <th className="text-left font-semibold px-4 py-3">Last Step</th>
                <th className="text-left font-semibold px-4 py-3">Updated</th>
                <th className="text-left font-semibold px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td className="px-4 py-8 text-gray-500" colSpan={9}>
                    Loading…
                  </td>
                </tr>
              ) : filtered.length ? (
                filtered.map((x) => (
                  <tr key={x._id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <Badge className={`${statusBadge[x.status] || "bg-gray-100 text-gray-700"}`}>
                        {x.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-800">{x.city || "-"}</td>
                    <td className="px-4 py-3 text-gray-800">{x.schoolName || "-"}</td>
                    <td className="px-4 py-3 text-gray-800">{x.phoneNumber || "-"}</td>
                    <td className="px-4 py-3 text-gray-800">{x.contactName || "-"}</td>
                    <td className="px-4 py-3 text-gray-800">{x.designation || "-"}</td>
                    <td className="px-4 py-3 text-gray-600">{x.lastStep || "-"}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {x.updatedAt ? new Date(x.updatedAt).toLocaleString() : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="destructive"
                        size="sm"
                        className="rounded-lg"
                        onClick={() => handleDelete(x._id)}
                        disabled={deletingId === x._id}
                      >
                        {deletingId === x._id ? "Deleting..." : "Delete"}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-8 text-gray-500" colSpan={9}>
                    No leads found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

